import { Form, FormItem, FormList } from '@magicdoor/form';
import { LabeledAddressInput, useLocalization } from '@magicdoor/ui';
import { createEffect, createMemo, createSignal, onMount, Show, untrack } from 'solid-js';
import PlusIcon from '~/assets/images/common/plus.svg?component-solid';
import IconRemove from '~/assets/images/common/remove.svg?component-solid';
import { Button } from '~/components/common/Buttons';
import { DropdownMenu } from '~/components/common/DropdownMenu';
import { FormListRemoverWrapper } from '~/components/common/FormExtensions';
import { Section } from '~/components/common/FormSectionTitle';
import { Checkbox } from '~/components/common/Inputs/Checkbox';
import DueDateInputField from '~/components/common/Inputs/DueDateInputField';
import { LabeledTextInput } from '~/components/common/Inputs/LabeledTextInput';
import ExpandableSection from '~/components/common/expandables/ExpandableSection';
import { LanguageSelect } from '~/components/languages';
import { YearRange } from '~/components/ui/Calendar';
import { cn } from '~/utils/classnames';
import { genderTypes } from '~/utils/constant';
import { whenThisDayIsNotBirthDay } from '~/utils/date';
import { tenantName } from '~/utils/formRules';
import type { BetterForm } from '@magicdoor/form';
import type { Component } from 'solid-js';

const DriverLicenseInput = () => {
  const { t } = useLocalization();

  return (
    <div class="grid grid-cols-2 gap-2">
      <FormItem
        class="col-span-full lg:col-span-1"
        label={t('Driver license number')}
        placeholder={t('Driver license number')}
        formFieldName={['driversLicense', 'number']}
        component={LabeledTextInput}
      />
      <FormItem
        rules={[{ length: 2, message: t('{name} must be {length} characters', { name: t('Driver license state'), length: 2 }) }]}
        class="col-span-full lg:col-span-1"
        label={t('Driver license state')}
        placeholder={t('Driver license state')}
        formFieldName={['driversLicense', 'state']}
        component={LabeledTextInput}
      />
    </div>
  );
};

const EmergencyContacts = () => {
  const { t } = useLocalization();

  return (
    <FormList
      formFieldName={'emergencyContacts'}
      renderItemsGroup={({ index, remove }) => {
        return (
          <FormListRemoverWrapper index={index} remove={remove}>
            <div class="flex gap-5">
              <FormItem
                placeholder="Name"
                label={t(`Name`)}
                class="grow"
                formFieldName={['name']}
                component={LabeledTextInput}
                rules={[{ message: t(`Please input name`), required: true }]}
              />
              <FormItem
                class="grow"
                placeholder="Email"
                label={t(`Email address`)}
                formFieldName={['email']}
                component={LabeledTextInput}
              />
              <FormItem class="grow" placeholder="Phone" label={t(`Phone number`)} formFieldName={['phone']} component={LabeledTextInput} />
              <FormItem
                class="grow"
                placeholder="Relationship"
                label={t(`Relationship`)}
                formFieldName={['relationship']}
                component={LabeledTextInput}
              />
              <div class="ml-2 mt-8 flex cursor-pointer items-start" onClick={remove}>
                <IconRemove class="size-5 text-primary" />
              </div>
            </div>
          </FormListRemoverWrapper>
        );
      }}
      autoCombineFieldName={false}>
      {({ add }) => {
        return (
          <Button variant="white" onClick={add}>
            <PlusIcon /> {t('Add more')}
          </Button>
        );
      }}
    </FormList>
  );
};

export const AddOrEditTenantForm: Component<{
  form?: BetterForm.ContextValue;
  class?: string;
  isEdit?: boolean;
}> = (props) => {
  const { t } = useLocalization();

  const [isCompany, setIsCompany] = createSignal(false);

  const [expandableOpen, setExpandableOpen] = createSignal(false);

  const defaultLanguage = 'en';

  createEffect(() => {
    if (!props.form?.isValidate) {
      setExpandableOpen(true);
    }
  });

  const handleIsCompanyChange = (selected: string) => {
    const value = selected.toString() === 'true';
    setIsCompany(value);
  };

  onMount(() => {
    if (props.form) {
      const hasLastName = !!props.form?.formStore.lastName;
      const hasFirstName = !!props.form?.formStore.firstName;
      props.form.setFieldValue('isCompany', !hasLastName && hasFirstName);

      if (!props.isEdit) {
        props.form.setFieldValue('sendPortalInvite', true);
      }
    }
    setIsCompany(!!props.form?.formStore.isCompany);
  });

  createEffect(() => {
    if (isCompany() && props.form) {
      props.form.resetFields(['lastName']);
    }
  });

  const emailRules = createMemo(() => {
    untrack(() => {
      props.form?.setFormError('email', undefined);
    });
    const rules: BetterForm.Rule[] = [{ type: 'email', message: t('Email is invalid') }];
    if (!props.isEdit && props.form?.formStore.sendPortalInvite) {
      rules.push({ required: true, message: t('{name} is required', { name: t('Email address') }) });
    }
    return rules;
  });

  return (
    <Form
      initialValues={{
        currentAddress: {},
        emergencyContacts: [{}],
        language: defaultLanguage,
      }}
      defaultForm={props.form}
      class={cn('text-left', props.class)}>
      <Section title={t('Tenant information')} border>
        <div class="grid grid-cols-2 gap-4">
          <FormItem
            class="col-span-full md:col-span-1"
            options={[
              { label: t('Yes'), value: true },
              { label: t('No'), value: false },
            ]}
            label={t('Is company')}
            placeholder={t('Select')}
            onChangeMethodName="onChange"
            formFieldName="isCompany"
            component={DropdownMenu}
            onChange={handleIsCompanyChange}
          />
          <div class="col-span-full md:col-span-1" />
          <Show
            when={!isCompany()}
            fallback={
              <FormItem
                class="col-span-full md:col-span-1"
                label={t('Company name')}
                placeholder={t('Please enter the {name}', { name: t('Company name') })}
                component={LabeledTextInput}
                formFieldName="firstName"
                rules={[
                  { required: true, message: t('{name} is required', { name: t('Company name') }) },
                  { length: 150, message: t('{name} must be {length} characters or less', { name: t('Company name'), length: '150' }) },
                ]}
              />
            }>
            <FormItem
              id="add-tenant-form-firstName"
              class="col-span-full lg:col-span-1"
              rules={[{ message: t(`Please input first name`), required: true }, ...tenantName(t('First name'))]}
              formFieldName="firstName"
              label={t('First name')}
              component={LabeledTextInput}
              placeholder={t('Please enter')}
            />
            <FormItem
              id="add-tenant-form-lastName"
              class="col-span-full lg:col-span-1"
              rules={[{ message: t(`Please input last name`), required: true }, ...tenantName(t('Last name'))]}
              formFieldName="lastName"
              label={t('Last name')}
              component={LabeledTextInput}
              placeholder={t('Please enter')}
            />
          </Show>
        </div>
        <div class="grid grid-cols-3 gap-4 gap-y-6 pt-4">
          <FormItem
            id="add-tenant-form-email"
            class="col-span-full lg:col-span-1"
            formFieldName="email"
            label={t('Email address')}
            component={LabeledTextInput}
            placeholder={t('Please enter')}
            rules={emailRules()}
          />
          <FormItem
            id="add-tenant-form-phone"
            class="col-span-full lg:col-span-1"
            formFieldName="phone"
            label={t('Phone number')}
            component={LabeledTextInput}
            placeholder={t('Please enter')}
            rules={[{ type: 'phone', message: t('Phone is invalid') }]}
          />
          <FormItem
            id="add-tenant-form-language"
            class="col-span-full lg:col-span-1"
            label={t('Language')}
            placeholder={t('Select language')}
            onChangeMethodName="onChange"
            formFieldName="language"
            component={LanguageSelect}
          />
          <Show when={!props.isEdit}>
            <FormItem
              component={Checkbox}
              id="add-tenant-form-send-portal-invitation"
              class="col-span-full lg:col-span-1"
              label={t('Send portal invitation')}
              valuePropName="checked"
              showLabel
              formFieldName="sendPortalInvite"
            />
          </Show>
        </div>
      </Section>

      <ExpandableSection
        open={expandableOpen()}
        onOpenChange={setExpandableOpen}
        headerBorder
        class="px-0 text-base"
        triggerTitle={
          <span class="capitalize">
            {t('Other information')} <span class="text-xs text-text-level03">({t('Optional sections')})</span>
          </span>
        }>
        <div class="mx-1 mb-8 grid grid-cols-3 gap-4 gap-y-6">
          <FormItem
            class="col-span-full lg:col-span-1"
            formFieldName="dateOfBirth"
            label={t('Date of birth')}
            component={DueDateInputField}
            yearRange={YearRange.Birthday}
            disabledDate={whenThisDayIsNotBirthDay}
            rules={[{ type: 'dateOfBirth', message: t('Date of birth is invalid, must be between 18 years and 125 years old') }]}
          />
          <FormItem
            class="col-span-full lg:col-span-1"
            formFieldName="gender"
            label={t('Gender')}
            options={genderTypes}
            onChangeMethodName="onChange"
            component={DropdownMenu}
            placeholder={t('Select gender')}
          />
          <FormItem
            class="col-span-full lg:col-span-1"
            formFieldName="ssn"
            label={t('Social security number (ssn)')}
            component={LabeledTextInput}
            placeholder={t('Please enter')}
            rules={[{ type: 'ssn', message: t('Social security number is invalid') }]}
          />
          <div class="col-span-3 flex flex-col gap-y-8">
            <FormItem class="col-span-3" formFieldName="currentAddress" label={t('Current address')} component={LabeledAddressInput} />
          </div>
          <div class="col-span-3 grid grid-cols-2 gap-4">
            <DriverLicenseInput />
          </div>
        </div>

        <Section class="mx-1 mt-5" title={t(`Emergency contact`)} border>
          <EmergencyContacts />
        </Section>
      </ExpandableSection>
    </Form>
  );
};
